import React, { useState, useEffect } from 'react'
import { supabase } from '../supabaseClient'

export function PageSkeleton() {
  return (
    <>
      <style>{`
        @keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}
        .sk{background:linear-gradient(90deg,rgba(var(--fg-rgb),0.04) 25%,rgba(var(--fg-rgb),0.08) 50%,rgba(var(--fg-rgb),0.04) 75%);background-size:200% 100%;animation:shimmer 1.4s infinite;border-radius:10px;}
      `}</style>
      <div style={{minHeight:'100vh',background:'var(--bg-shell)',padding:'20px',boxSizing:'border-box'}}>
        <div style={{display:'flex',alignItems:'center',gap:'12px',marginBottom:'28px'}}>
          <div className="sk" style={{width:'80px',height:'28px'}}/>
          <div style={{flex:1}}/>
          <div className="sk" style={{width:'36px',height:'36px',borderRadius:'50%'}}/>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))',gap:'12px',marginBottom:'20px'}}>
          {[1,2,3,4].map(i=><div key={i} className="sk" style={{height:'80px'}}/>)}
        </div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'14px'}}>
          <div className="sk" style={{height:'220px'}}/>
          <div className="sk" style={{height:'220px'}}/>
        </div>
      </div>
    </>
  )
}

export default function LoginPage() {
  const [mode,     setMode]     = useState('login')
  const [email,    setEmail]    = useState('')
  const [password, setPassword] = useState('')
  const [newPass,  setNewPass]  = useState('')
  const [confirm,  setConfirm]  = useState('')
  const [name,     setName]     = useState('')
  const [busy,     setBusy]     = useState(false)
  const [err,      setErr]      = useState('')
  const [ok,       setOk]       = useState('')
  const [showPwd,  setShowPwd]  = useState(false)
  const [showNew,  setShowNew]  = useState(false)
  const [strength, setStrength] = useState(0)

  // Detect Supabase password recovery token in URL
  useEffect(() => {
    const hash = window.location.hash
    if (hash.includes('type=recovery') || hash.includes('type%3Drecovery')) {
      setMode('reset')
    }
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') setMode('reset')
    })
    return () => subscription.unsubscribe()
  }, [])

  function checkStrength(p) {
    let s = 0
    if (p.length >= 8)          s++
    if (/[A-Z]/.test(p))        s++
    if (/[0-9]/.test(p))        s++
    if (/[^A-Za-z0-9]/.test(p)) s++
    setStrength(s)
  }

  // ── LOGIN — fixed: always releases busy, clear error messages ────────────
  async function handleLogin(e) {
    e.preventDefault()
    setErr(''); setOk('')
    if (!email.trim())    return setErr('Please enter your email.')
    if (!password.trim()) return setErr('Please enter your password.')
    setBusy(true)
    try {
      const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password })
      if (error) {
        if (error.message.toLowerCase().includes('invalid'))
          setErr('Wrong email or password. Please try again.')
        else if (error.message.toLowerCase().includes('not confirmed'))
          setErr('Please confirm your email first. Check your inbox.')
        else if (error.message.toLowerCase().includes('too many'))
          setErr('Too many attempts. Please wait a minute and try again.')
        else
          setErr(error.message)
      }
      // Success → App.js onAuthStateChange handles redirect automatically
    } catch (e) {
      setErr('Network error. Please check your connection and try again.')
    } finally {
      setBusy(false)  // ALWAYS release — no matter what
    }
  }

  // ── REGISTER ─────────────────────────────────────────────────────────────
  async function handleRegister(e) {
    e.preventDefault()
    setErr(''); setOk('')
    if (!name.trim())     return setErr('Please enter your full name.')
    if (!email.trim())    return setErr('Please enter your email.')
    if (password.length < 6) return setErr('Password must be at least 6 characters.')
    setBusy(true)
    try {
      const { error } = await supabase.auth.signUp({
        email: email.trim(), password,
        options: { data: { full_name: name.trim(), role: 'student' } }
      })
      if (error) setErr(error.message)
      else setOk('Account created! Check your email to confirm, then log in.')
    } catch(e) {
      setErr('Network error. Please check your connection and try again.')
    } finally {
      setBusy(false)
    }
  }

  // ── FORGOT PASSWORD ───────────────────────────────────────────────────────
  async function handleForgot(e) {
    e.preventDefault()
    setErr(''); setOk('')
    if (!email.trim()) return setErr('Please enter your email.')
    setBusy(true)
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: window.location.origin,
      })
      if (error) {
        console.error('Password reset request failed:', error)
        const message = [error.message, error.msg, error.error_description]
          .find(value => typeof value === 'string' && value.trim() && value.trim() !== '{}')
        setErr(message && message !== '{}' ? message : 'Password reset is not configured for this website. Please contact support.')
      }
      else setOk('Reset link sent! Check your inbox — click the link to set a new password.')
    } catch(e) {
      setErr('Network error. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  // ── RESET PASSWORD ────────────────────────────────────────────────────────
  async function handleReset(e) {
    e.preventDefault()
    setErr('')
    if (newPass !== confirm)  return setErr('Passwords do not match.')
    if (newPass.length < 6)   return setErr('Password must be at least 6 characters.')
    setBusy(true)
    try {
      const { error } = await supabase.auth.updateUser({ password: newPass })
      if (error) {
        setErr(error.message)
      } else {
        setOk('Password updated! Redirecting to login...')
        window.history.replaceState(null, '', window.location.pathname)
        setTimeout(() => { setMode('login'); setOk(''); setNewPass(''); setConfirm('') }, 2000)
      }
    } catch(e) {
      setErr('Network error. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  // ── Styles ────────────────────────────────────────────────────────────────
  const sColor = ['','#f87171','#f4a335','#60a5fa','#34d399']
  const sLabel = ['','Weak','Fair','Good','Strong']
  const inp = { width:'100%', padding:'11px 14px', background:'rgba(var(--fg-rgb),0.06)', border:'0.5px solid rgba(var(--fg-rgb),0.12)', borderRadius:'10px', color:'rgba(var(--fg-rgb),var(--a85))', fontSize:'14px', outline:'none', fontFamily:'inherit', boxSizing:'border-box' }
  const btn = { width:'100%', padding:'13px', background:'#1e90ff', color:'#fff', border:'none', borderRadius:'10px', fontSize:'15px', fontWeight:700, cursor:busy?'not-allowed':'pointer', opacity:busy?0.7:1, fontFamily:'inherit', transition:'opacity 0.15s' }
  const lnk = { background:'none', border:'none', color:'#5aabff', fontSize:'12px', cursor:'pointer', fontFamily:'inherit', padding:0, textDecoration:'underline' }

  const TITLES = { login:'Welcome back', register:'Create account', forgot:'Reset password', reset:'Set new password' }
  const SUBS   = { login:'Log in to UniEDD LMS', register:'Join UniEDD as a student', forgot:"We'll email you a reset link", reset:'Choose a strong new password' }

  return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'var(--bg-shell)', padding:'20px', boxSizing:'border-box' }}>
      <div style={{ width:'100%', maxWidth:'400px', background:'rgba(var(--fg-rgb),0.03)', border:'0.5px solid rgba(var(--fg-rgb),0.08)', borderRadius:'20px', padding:'32px 28px' }}>

        {/* Logo */}
        <div style={{textAlign:'center', marginBottom:'28px'}}>
          <div style={{fontFamily:"'Arial Black',sans-serif", fontWeight:900, marginBottom:'6px'}}>
            <span style={{color:'#1e90ff', fontSize:'28px'}}>UNI</span>
            <span style={{color:'#e87c1e', fontSize:'28px'}}>EDD</span>
          </div>
          <div style={{fontSize:'15px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a80))'}}>{TITLES[mode]}</div>
          <div style={{fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a35))', marginTop:'3px'}}>{SUBS[mode]}</div>
        </div>

        {/* ── RESET PASSWORD FORM ── */}
        {mode === 'reset' && (
          <form onSubmit={handleReset} style={{display:'grid', gap:'12px'}}>
            <div style={{padding:'10px 14px', background:'rgba(30,144,255,0.08)', border:'0.5px solid rgba(30,144,255,0.2)', borderRadius:'10px', fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a50))', textAlign:'center'}}>
              🔐 Enter your new password below
            </div>

            <div style={{position:'relative'}}>
              <input type={showNew?'text':'password'} placeholder="New password" required
                value={newPass} onChange={e=>{setNewPass(e.target.value);checkStrength(e.target.value)}}
                style={{...inp, paddingRight:'44px'}} autoFocus/>
              <button type="button" onClick={()=>setShowNew(v=>!v)}
                style={{position:'absolute',right:'12px',top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',fontSize:'16px',padding:0}}>
                {showNew?'🙈':'👁'}
              </button>
            </div>

            {newPass.length > 0 && (
              <div>
                <div style={{display:'flex',gap:'4px',marginBottom:'4px'}}>
                  {[1,2,3,4].map(i=>(
                    <div key={i} style={{flex:1,height:'3px',borderRadius:'3px',transition:'background 0.3s',
                      background:i<=strength?sColor[strength]:'rgba(var(--fg-rgb),0.1)'}}/>
                  ))}
                </div>
                <div style={{fontSize:'10px',color:sColor[strength],fontWeight:700}}>{sLabel[strength]}</div>
              </div>
            )}

            <div style={{position:'relative'}}>
              <input type="password" placeholder="Confirm new password" required
                value={confirm} onChange={e=>setConfirm(e.target.value)}
                style={{...inp, borderColor:confirm&&confirm!==newPass?'#f87171':undefined}}/>
              {confirm && (
                <span style={{position:'absolute',right:'12px',top:'50%',transform:'translateY(-50%)',fontSize:'14px'}}>
                  {confirm===newPass?'✅':'❌'}
                </span>
              )}
            </div>

            <div style={{fontSize:'11px',display:'grid',gap:'3px'}}>
              {[
                {label:'At least 8 characters', ok:newPass.length>=8},
                {label:'One uppercase letter',  ok:/[A-Z]/.test(newPass)},
                {label:'One number',            ok:/[0-9]/.test(newPass)},
              ].map((r,i)=>(
                <div key={i} style={{color:r.ok?'#34d399':'rgba(var(--fg-rgb),var(--a30))'}}>{r.ok?'✓':'○'} {r.label}</div>
              ))}
            </div>

            {err && <div style={{fontSize:'12px',color:'#f87171',padding:'8px 12px',background:'rgba(239,68,68,0.1)',borderRadius:'8px'}}>{err}</div>}
            {ok  && <div style={{fontSize:'12px',color:'#34d399',padding:'8px 12px',background:'rgba(16,185,129,0.1)',borderRadius:'8px'}}>{ok}</div>}

            <button type="submit" disabled={busy||newPass!==confirm||newPass.length<6}
              style={{...btn, opacity:busy||newPass!==confirm||newPass.length<6?0.5:1}}>
              {busy?'⏳ Updating...':'🔐 Set New Password'}
            </button>

            <button type="button" style={lnk} onClick={()=>{setMode('login');setErr('');setOk('');window.history.replaceState(null,'',window.location.pathname)}}>
              ← Back to login
            </button>
          </form>
        )}

        {/* ── LOGIN / REGISTER / FORGOT ── */}
        {mode !== 'reset' && (
          <form
            onSubmit={mode==='login' ? handleLogin : mode==='register' ? handleRegister : handleForgot}
            style={{display:'grid', gap:'12px'}}
          >
            {mode==='register' && (
              <input type="text" placeholder="Full name" required value={name}
                onChange={e=>setName(e.target.value)} style={inp} autoFocus/>
            )}

            <input type="email" placeholder="Email address" required value={email}
              onChange={e=>setEmail(e.target.value)} style={inp}
              autoFocus={mode==='login'||mode==='forgot'}/>

            {mode!=='forgot' && (
              <div style={{position:'relative'}}>
                <input type={showPwd?'text':'password'} placeholder="Password" required
                  value={password} onChange={e=>setPassword(e.target.value)}
                  style={{...inp, paddingRight:'44px'}}/>
                <button type="button" onClick={()=>setShowPwd(v=>!v)}
                  style={{position:'absolute',right:'12px',top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',fontSize:'16px',padding:0}}>
                  {showPwd?'🙈':'👁'}
                </button>
              </div>
            )}

            {err && <div style={{fontSize:'12px',color:'#f87171',padding:'8px 12px',background:'rgba(239,68,68,0.1)',borderRadius:'8px'}}>{err}</div>}
            {ok  && <div style={{fontSize:'12px',color:'#34d399',padding:'8px 12px',background:'rgba(16,185,129,0.1)',borderRadius:'8px'}}>{ok}</div>}

            <button type="submit" style={btn} disabled={busy}>
              {busy
                ? '⏳ Please wait...'
                : mode==='login' ? 'Log In'
                : mode==='register' ? 'Create Account'
                : 'Send Reset Link'
              }
            </button>
          </form>
        )}

        {/* Footer links */}
        <div style={{marginTop:'18px', display:'grid', gap:'8px', textAlign:'center'}}>
          {mode==='login' && <>
            <button style={lnk} onClick={()=>{setMode('forgot');setErr('');setOk('')}}>Forgot password?</button>
            <div style={{fontSize:'12px',color:'rgba(var(--fg-rgb),var(--a30))'}}>
              New student?{' '}
              <button style={lnk} onClick={()=>{setMode('register');setErr('');setOk('')}}>Create an account</button>
            </div>
          </>}
          {mode==='register' && (
            <div style={{fontSize:'12px',color:'rgba(var(--fg-rgb),var(--a30))'}}>
              Already have an account?{' '}
              <button style={lnk} onClick={()=>{setMode('login');setErr('');setOk('')}}>Log in</button>
            </div>
          )}
          {mode==='forgot' && (
            <button style={lnk} onClick={()=>{setMode('login');setErr('');setOk('')}}>← Back to login</button>
          )}
        </div>

      </div>
    </div>
  )
}
